/**
 * @typedef {ReturnType<typeof buildCarousel>} Carousel
 */

/**
 * @param {Carousel} carousel
 */
const updateCarouselState = (carousel) => {
  const {
    scrollableArea,
    scrollBackwardsButton,
    scrollForwardsButton,
    navigationElement,
    snapPoints,
  } = carousel;

  let navStart = scrollableArea.scrollLeft <= 10; // 10px tolerance
  let navEnd =
    scrollableArea.scrollLeft + scrollableArea.clientWidth >=
    scrollableArea.scrollWidth - 10; // 10px tolerance

  scrollBackwardsButton.disabled = navStart;
  scrollForwardsButton.disabled = navEnd;

  if (navigationElement) {
    let currentIndex = 1; // navStart

    if (navEnd) {
      currentIndex = snapPoints.length - 1;
    } else {
      currentIndex = getClosestSnapPointIndex(carousel);
    }

    navigationElement
      .querySelectorAll("button>div")
      .forEach((button, index) => {
        if (index === currentIndex) {
          button.classList.remove("bg-neutral-200"); // Remove the gray background if it exists
          button.classList.add("bg-purple-500"); // Add the purple background
        } else {
          button.classList.remove("bg-purple-500"); // Remove the purple background if it exists
          button.classList.add("bg-neutral-200"); // Add the gray background
        }
      });
  }
};

/**
 * @param {Carousel} carousel
 * @returns {number | undefined}
 */
const getClosestSnapPointIndex = (carousel) => {
  const currentScroll = carousel.scrollableArea.scrollLeft;

  const closest = carousel.snapPoints.reduce((prev, curr) =>
    Math.abs(curr - currentScroll) < Math.abs(prev - currentScroll)
      ? curr
      : prev,
  );

  return carousel.snapPoints.indexOf(closest);
};

///**
// * @param {Carousel} carousel
// * @returns {number | undefined}
// */
//const getNextSnapPoint = (carousel) => {
//  const currentScroll = carousel.scrollableArea.scrollLeft;
//  const snapPoints = carousel.snapPoints.filter(
//    (point) => Math.abs(point - currentScroll) > 10
//  );
//  return snapPoints.find((point) => point > currentScroll);
//};

// TODO: Uncomment to enable autoplay - feature will be added later, currently it's not working as expected
// /**
//  * @param {Carousel} carousel
//  */
// const handlePlayPause = (carousel) => {
//   carousel.isPlaying = !carousel.isPlaying;
//   carousel.playPauseButton.childNodes[0].textContent = carousel.isPlaying
//     ? "Pause"
//     : "Play";

//   if (carousel.isPlaying) {
//     carousel.autoplayIntervalId = setInterval(() => {
//       const nextPoint = getNextSnapPoint(carousel);

//       if (nextPoint !== undefined) {
//         carousel.scrollableArea.scrollTo({
//           left: nextPoint,
//           behavior: "smooth",
//         });
//       } else {
//         // Rewind to the beginning and continue autoplay
//         carousel.scrollableArea.scrollTo({ left: 0, behavior: "smooth" });
//       }
//     }, 3000);
//   } else {
//     clearInterval(carousel.autoplayIntervalId);
//     carousel.autoplayIntervalId = undefined;
//   }
// };

/**
 * @param {HTMLElement} scrollableArea
 */
const getSnapPoints = (scrollableArea) => {
  const snapPoints = [];
  const items = scrollableArea.children; // Get all child elements
  const parentOffset = scrollableArea.offsetLeft; // Parent offset from view start

  for (let item of items) {
    if (item instanceof HTMLDivElement) {
      let childOffset = item.offsetLeft - parentOffset;
      snapPoints.push(childOffset); // Each item's offset from the left is a snap point
    }
  }

  return snapPoints.map((point, index) => (index === 0 ? 0 : point));
};

/**
 * @param {"backwards" | "forwards"} direction
 * @param {Carousel} carousel
 */
const scrollToSnapPoint = (carousel, direction, index) => {
  const { scrollableArea, snapPoints } = carousel;

  const currentIndex = getClosestSnapPointIndex(carousel);
  const offset = direction === "forwards" ? 1 : -1;
  let nextIndex = index > -1 ? index : currentIndex + offset;

  // Uncomment below to allow infinite looping
  // nextIndex = (nextIndex + snapPoints.length) % snapPoints.length;

  // Don't scroll if out of bounds
  if (nextIndex < 0) nextIndex = 0;
  else if (nextIndex >= snapPoints.length) nextIndex = snapPoints.length - 1;

  const nextSnap = snapPoints[nextIndex];
  updateCarouselState(carousel);

  scrollableArea.scrollTo({
    left: nextSnap,
    behavior: "smooth",
  });
};

/**
 * @param {HTMLElement} el
 */
const buildCarousel = (el) => {
  const scrollableArea = el.querySelector("[data-scrollable-area]");
  const [back, forward] = ["backwards", "forwards"].map((attr) =>
    el.querySelector(`[data-carousel-button='${attr}']`),
  );

  if (!(scrollableArea instanceof HTMLElement && back && forward)) {
    console.error("Missing carousel elements");
    throw new Error("Missing carousel elements");
  }

  // Get current carousel navigation
  const navigation = el.querySelector("[data-carousel-nav]");

  return {
    carouselEl: el,
    scrollableArea,
    snapPoints: getSnapPoints(scrollableArea),
    scrollBackwardsButton: /** @type {HTMLButtonElement} */ (back),
    scrollForwardsButton: /** @type {HTMLButtonElement} */ (forward),
    navigationElement: /** @type {HTMLElement} */ (navigation),
    // TODO: Uncomment to enable autoplay - feature will be added later
    // playPauseButton: /** @type {HTMLButtonElement} */ (playPause),
    isPlaying: false,
    autoplayIntervalId: undefined,
  };
};

/**
 * @param {HTMLElement} el
 */
const handleCarousel = (el) => {
  const carousel = buildCarousel(el);
  if (!carousel) return;

  carousel.scrollBackwardsButton.addEventListener("click", () =>
    scrollToSnapPoint(carousel, "backwards"),
  );
  carousel.scrollForwardsButton.addEventListener("click", () =>
    scrollToSnapPoint(carousel, "forwards"),
  );
  // TODO: Uncomment to enable autoplay - feature will be added later
  // carousel.playPauseButton.addEventListener("click", () =>
  //   handlePlayPause(carousel)
  // );
  carousel.scrollableArea.addEventListener("scroll", () =>
    updateCarouselState(carousel),
  );

  window.addEventListener("resize", () => {
    carousel.snapPoints = getSnapPoints(carousel.scrollableArea);
    updateCarouselState(carousel);
  });

  window.addEventListener("load", () => {
    carousel.snapPoints = getSnapPoints(carousel.scrollableArea);
    updateCarouselState(carousel);
  });

  carousel.navigationElement
    .querySelectorAll("button")
    .forEach((button, index) => {
      button.addEventListener("click", () => {
        scrollToSnapPoint(carousel, "backwards", index);
      });
    });
};

window.addEventListener("DOMContentLoaded", () =>
  document.getElementsByName("carousel").forEach(handleCarousel),
);
